import type {
  ApprovalDetail,
  ApprovalRequestedPayload,
  RunEvent,
  SecretRefUsage,
} from "@carbon-operations/contracts";
import { approvalRequestFor, approvalRequestMatchesRun, eventsMatchRunBoundary } from "./contract-builders.js";
import { approvalActionContextDigestMatches } from "./approval-context-integrity.js";

type Boundary = { tenantId: string; workspaceId: string };
type WorkspaceMembership = { roles: readonly string[]; scopes: readonly string[] };

const anyRole = ["owner", "admin", "operator", "approver", "auditor", "viewer"];

/**
 * Which read-only record pages a workspace-local membership may open; the same role and scope rules the server
 * applies. The server rechecks every request, so this only decides what to request and what to explain.
 */
export function recordsAccess(membership: WorkspaceMembership) {
  const role = (allowed: string[]) => membership.roles.some((value) => allowed.includes(value));
  const scope = (value: string) => membership.scopes.includes(value);
  return {
    operators: role(anyRole) && scope("readiness:read"),
    connections: role(anyRole) && scope("readiness:read"),
    audit: role(["owner", "admin", "auditor"]) && scope("runs:read"),
    settings: role(["owner", "admin"]) && scope("readiness:read"),
  };
}

/** Each workspace record response must name the selected tenant and workspace, or nothing is rendered. */
export function recordsMatchBoundary(
  response: { schema: string; tenant_id: string; workspace_id: string },
  schema: string,
  boundary: Boundary,
): boolean {
  return response.schema === schema && response.tenant_id === boundary.tenantId &&
    response.workspace_id === boundary.workspaceId;
}

export function approvalMatchesBoundary(detail: ApprovalDetail, approvalId: string, boundary: Boundary): boolean {
  return detail.schema === "carbon.operations.approval/v1" && detail.approval_id === approvalId &&
    detail.workspace_id === boundary.workspaceId && detail.run.tenant_id === boundary.tenantId &&
    detail.run.workspace_id === boundary.workspaceId && detail.run.run_id === detail.run_id;
}

/**
 * The run-detail matching rule, applied to an approval opened by its ID: the run names this approval, its ordered
 * events carry a valid approval.requested for it, and that event's digest and expiry equal the server's approval.
 * Only then may the decision form be offered; the digest is never taken from the URL.
 */
export function approvalDecisionEvidence(
  detail: ApprovalDetail,
  events: RunEvent[],
  boundary: Boundary,
): ApprovalRequestedPayload | null {
  if (detail.run.approval_id !== detail.approval_id) return null;
  if (!eventsMatchRunBoundary(events, { ...boundary, runId: detail.run_id })) return null;
  const request = approvalRequestFor(events, detail.approval_id);
  if (request === null || request.expected_action_sha256 !== detail.expected_action_sha256 ||
      Date.parse(request.expires_at) !== Date.parse(detail.expires_at) ||
      !approvalRequestMatchesRun(request, detail.run)) return null;
  return request;
}

/** Digest-verifying evidence required by every UI decision surface. */
export async function verifiedApprovalDecisionEvidence(
  detail: ApprovalDetail,
  events: RunEvent[],
  boundary: Boundary,
): Promise<ApprovalRequestedPayload | null> {
  const evidence = approvalDecisionEvidence(detail, events, boundary);
  return evidence !== null && await approvalActionContextDigestMatches(evidence) ? evidence : null;
}

/** Why a decision cannot be offered, or null when it can. */
export function decisionUnavailableReason(
  detail: ApprovalDetail,
  evidence: ApprovalRequestedPayload | null,
  access: { decide: boolean; readRuns: boolean },
  nowMs: number,
): string | null {
  if (detail.state !== "pending") return `This approval is ${detail.state}; it no longer accepts a decision.`;
  if (detail.run.state !== "awaiting_approval") return "The run is no longer awaiting this approval.";
  if (Date.parse(detail.expires_at) <= nowMs) return "This approval has expired.";
  if (!access.decide) return "The active membership lacks an eligible role or the approvals:decide scope.";
  if (!access.readRuns) {
    return "Deciding reads the run's ordered events, which needs the runs:read scope. Your membership does not include it.";
  }
  if (evidence === null) {
    return "The run's ordered event stream did not provide an approval.requested event whose approval ID, recomputed action-context SHA-256, run facts and expiry match this approval.";
  }
  return null;
}

export const auditEventTypes = [
  "run.queued", "policy.rejected", "approval.requested", "approval.decided", "run.started", "action.started",
  "action.succeeded", "action.failed", "artifact.created", "run.succeeded", "run.failed", "run.cancel_requested",
  "run.canceled", "run.expired", "runner.attempt.retry_scheduled", "artifact.release_authorized",
  "artifact.cleanup.completed", "artifact.cleanup.quarantined", "workspace.readiness.configured",
  "workspace.readiness.verified", "workspace.readiness.reverification_queued", "workspace.readiness.canary_failed",
  "outbox.replayed", "outbox.integrity_quarantined", "outbox.integrity_quarantine_released", "event_sink.key_rotated",
  "security.containment_applied", "client.onboarding.applied",
] as const;

export type AuditFilters = { runId: string; eventType: string; from: string; to: string };

/** Query string for one audit page; filter values are validated here and again by the server. */
export function auditQuery(filters: AuditFilters, cursor?: string): { query: URLSearchParams } | { error: string } {
  const runId = filters.runId.trim();
  const eventType = filters.eventType.trim();
  if (runId !== "" && !/^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/.test(runId)) return { error: "The run ID is not a valid run reference." };
  if (eventType !== "" && !/^[a-z][a-z0-9_]*(\.[a-z0-9_]+)*$/.test(eventType)) return { error: "The event type is not valid." };
  const from = filters.from === "" ? null : localInstant(filters.from);
  const to = filters.to === "" ? null : localInstant(filters.to);
  if (from === undefined || to === undefined) return { error: "The time range is not a valid date and time." };
  if (from !== null && to !== null && Date.parse(from) > Date.parse(to)) return { error: "The start of the range is after its end." };
  return {
    query: new URLSearchParams({
      limit: "50",
      ...(runId === "" ? {} : { run_id: runId }),
      ...(eventType === "" ? {} : { event_type: eventType }),
      ...(from === null ? {} : { from }),
      ...(to === null ? {} : { to }),
      ...(cursor === undefined ? {} : { cursor }),
    }),
  };
}

function localInstant(value: string): string | undefined {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) ? new Date(milliseconds).toISOString() : undefined;
}

export function verificationLabel(verification: { hash: boolean; link: boolean }): {
  text: string; tone: "success" | "danger";
} {
  if (verification.hash && verification.link) return { text: "Hash and link verified", tone: "success" };
  if (!verification.hash && !verification.link) return { text: "Hash and link mismatch", tone: "danger" };
  return verification.hash ? { text: "Link mismatch", tone: "danger" } : { text: "Hash mismatch", tone: "danger" };
}

export function usageLabel(usage: SecretRefUsage): string {
  const labels: Record<SecretRefUsage, string> = {
    event_sink_signing: "Event sink signing key",
    object_store_credential: "Object store credential",
    artifact_encryption: "Artifact encryption key",
    runner_job: "Runner job secret",
  };
  return labels[usage];
}

const policyLabels: Record<string, string> = {
  approval_mode: "Approval mode",
  separation_of_duties: "Separation of duties",
  approval_required_capabilities: "Capabilities requiring approval",
  approval_policy_version: "Approval policy version",
  execution_enabled: "Execution enabled (stored)",
  readiness_revision: "Readiness revision (stored)",
};

/** Every stored policy key, labelled when known, in a stable order; nothing is shown that is not stored. */
export function policyEntries(policy: Record<string, unknown>): Array<{ key: string; label: string; value: string }> {
  return Object.keys(policy).sort((a, b) => {
    const known = (key: string) => key in policyLabels ? Object.keys(policyLabels).indexOf(key) : Number.MAX_SAFE_INTEGER;
    return known(a) - known(b) || a.localeCompare(b);
  }).map((key) => ({ key, label: policyLabels[key] ?? key, value: policyValue(policy[key]) }));
}

function policyValue(value: unknown): string {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.length === 0 ? "None" : value.map((item) => policyValue(item)).join(", ");
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (value === null) return "Not set";
  return JSON.stringify(value);
}
